import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { map, sortBy } from 'lodash'
import GoogleAd from '@components/ads/GoogleAd.tsx'
import GenericAd from '@components/ads/GenericAd.tsx'
import TimetoastTemporal from '@base/timetoast_temporal.js'
import { getPixelDataURL } from '@utils/canvas.js'
import { ThemeContext } from '@base/ThemeContext.js'
import Marker from './marker.jsx'
import Timespan from './timespan.jsx'

export default class ListTimeline extends PureComponent {
  constructor(props) {
    super(props)
    // mix up ad types
    this.state = {
      adMixSeed: Math.random(),
    }
  }

  getDisplayAdContent(key) {
    const { showAds, environment, adChannel } = this.props
    return (
      <li key={key} className="list-timeline__item timeline-item timeline-item--viewer reclame reclame--in-content">
        <GoogleAd
          client="ca-pub-3637961829875093"
          slot="3833790973"
          environment={environment}
          showAds={showAds}
          adFormat="auto"
          adChannel={adChannel}
        />
      </li>
    )
  }

  getNativeInfeedAdContent(key) {
    const { showAds, environment, adChannel } = this.props
    return (
      <li key={key} className="list-timeline__item timeline-item timeline-item--viewer reclame reclame--in-content">
        <GoogleAd
          client="ca-pub-3637961829875093"
          slot="5307148571"
          environment={environment}
          showAds={showAds}
          adFormat="fluid"
          adLayout="image-top"
          adLayoutKey="-8j+1w-dx+ec+gk"
          adChannel={adChannel}
        />
      </li>
    )
  }

  getOtherAdNetworkAdSrc() {
    return window.Timetoast.someOtherAdSrc
  }

  getOtherAdContent(key) {
    const { showAds, environment } = this.props
    return (
      <li key={key} className="list-timeline__item timeline-item timeline-item--viewer reclame reclame--in-content">
        <GenericAd src={this.getOtherAdNetworkAdSrc()} environment={environment} showAds={showAds} />
      </li>
    )
  }

  getTimelineItemsContent() {
    const { markers, timespans, photos } = this.props
    const colorScheme = this.context
    const sorterFn = item => TimetoastTemporal.parse(item.date || item.start_date).getTime()
    const allItems = markers.concat(timespans)
    const sortedItems = sortBy(allItems, [sorterFn])

    let itemCounter = 0
    let items = map(sortedItems, item => {
      const lazyLoadImage = itemCounter >= 2
      itemCounter++
      if (item.hasOwnProperty('start_date')) {
        // timespan
        return <Timespan key={`list-timespan-item-${item.id}`} data={item} colorScheme={colorScheme} />
      } else {
        // marker
        let photoData = null
        if (item.photo_ids.length > 0) {
          const photoIdString = item.photo_ids[0].toString()
          photoData = photos[photoIdString]
        }
        return (
          <Marker
            key={`list-marker-item-${item.id}`}
            data={item}
            photoData={photoData}
            colorScheme={colorScheme}
            lazyLoadImage={lazyLoadImage}
          />
        )
      }
    })
    items = this.injectAds(items)
    const listStyle = { backgroundImage: `url(${getPixelDataURL(colorScheme.tertiary)})` }
    return (
      <ul className="unstyled-list list-timeline__list" style={listStyle}>
        {items}
      </ul>
    )
  }

  getAdNetwork() {
    return window.Timetoast.adNetwork
  }

  getAdContent(key) {
    const adNetwork = this.getAdNetwork()
    if (adNetwork === 'some-other-ad-network') {
      return this.getOtherAdContent(key)
    } else {
      // default to adsense
      const { adMixSeed } = this.state
      return adMixSeed < 0.2 ? this.getNativeInfeedAdContent(key) : this.getDisplayAdContent(key)
    }
  }

  injectAds(items) {
    const { showAds } = this.props
    const adPositions = [2, 7, 10, 15, 18, 23] // note 0 indexed so injects ads after item (e.g. 3 will show after timeline item 3)
    if (showAds && items.length >= adPositions[0]) {
      const numItems = items.length
      const ads = [this.getAdContent(1)]
      if (numItems >= adPositions[1]) {
        ads.push(this.getAdContent(2))
      }
      if (numItems >= adPositions[2]) {
        ads.push(this.getAdContent(3))
      }
      if (numItems >= adPositions[3]) {
        ads.push(this.getAdContent(4))
      }
      if (numItems >= adPositions[4]) {
        ads.push(this.getAdContent(5))
      }
      if (numItems >= adPositions[5]) {
        ads.push(this.getAdContent(6))
      }
      if (numItems >= adPositions[6]) {
        ads.push(this.getAdContent(7))
      }
      if (numItems >= adPositions[7]) {
        ads.push(this.getAdContent(8))
      }

      // now inject ad into timeline items
      const numAds = ads.length
      for (let i = 0; i < numAds; i++) {
        items.splice(adPositions[i], 0, ads[i])
      }
    }
    return items
  }

  getNoItemsContent() {
    return (
      <div className="p">
        <p>This timeline doesn't have any content.</p>
      </div>
    )
  }

  render() {
    const { markers, timespans } = this.props
    const colorScheme = this.context
    const backgroundColor = colorScheme.primary
    const numItems = markers.length + timespans.length
    const content = numItems > 0 ? this.getTimelineItemsContent() : this.getNoItemsContent()
    const style = { backgroundColor }
    return (
      <div className="list-timeline list-timeline--classic" style={style}>
        {content}
      </div>
    )
  }
}

ListTimeline.contextType = ThemeContext

ListTimeline.propTypes = {
  markers: PropTypes.array.isRequired,
  timespans: PropTypes.array.isRequired,
  photos: PropTypes.object,
  environment: PropTypes.string.isRequired,
  showAds: PropTypes.bool.isRequired,
  adChannel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // could be number, or string with multiple numbers separated by + sign
}
ListTimeline.defaultProps = {
  markers: [],
  timespans: [],
  showAds: false,
}
